import { Fragment, useMemo, useState, type ReactElement } from 'react';
import { Button, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiExpandButton,
  PiSortHeader,
  piCount,
} from '../pi-kit';
import {
  ariaSort,
  compareValues,
  eventDateText,
  eventKey,
  parseSortState,
  splitIntoGroups,
  type SchoolAggregate,
  type SortState,
} from './events-model';
import { CompanyName, FormatBadge, GroupHeaderRow, GroupHeading } from './events-ui';

/**
 * By school tab: every school that took part in the listed events, split
 * Institutions / Partners, with what it got across them. A row expands inline
 * to the events behind the figures, newest first.
 *
 * ⚠️ NO TOTALS ROW. "Shared reach" is the same registrants counted once per
 * school on a virtual event; a column sum would double-count them.
 *
 * Six data columns, so no ColumnManager (the kit rule starts above six).
 * "Show more" reveals 50 schools at a time, counted across both groups.
 */

type Column = 'school' | 'events' | 'booth' | 'presentation' | 'reach' | 'sharedReach';
const COLUMNS: readonly Column[] = [
  'school',
  'events',
  'booth',
  'presentation',
  'reach',
  'sharedReach',
];
const DEFAULT_SORT: SortState<Column> = { column: 'booth', dir: 'desc' };
const parseSort = parseSortState(COLUMNS);
const PAGE_SIZE = 50;
const COL_SPAN = 7;

function valueOf(row: SchoolAggregate, column: Column): number | string {
  switch (column) {
    case 'school':
      return row.name;
    case 'events':
      return row.events;
    case 'booth':
      return row.boothConnections;
    case 'presentation':
      return row.presentationAttendees;
    case 'reach':
      return row.reach;
    case 'sharedReach':
      return row.sharedReach;
  }
}

function eventsSplitText(row: SchoolAggregate): string {
  const parts: string[] = [];
  if (row.inPersonEvents > 0) parts.push(`${piCount(row.inPersonEvents)} in person`);
  if (row.virtualEvents > 0) parts.push(`${piCount(row.virtualEvents)} virtual`);
  return parts.join(' · ');
}

const reachText = (row: SchoolAggregate) => (row.inPersonEvents > 0 ? piCount(row.reach) : '—');
const sharedReachText = (row: SchoolAggregate) =>
  row.virtualEvents > 0 ? piCount(row.sharedReach) : '—';

/** The events behind one school's figures, newest first. */
function SchoolEventsPanel({ row }: { row: SchoolAggregate }): ReactElement {
  return (
    <>
      <div className="max-md:hidden">
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH className="min-w-32">Date</TH>
                <TH className="min-w-56">Event</TH>
                <TH className="min-w-24">Format</TH>
                <TH numeric className="min-w-28">Booth connections</TH>
                <TH numeric className="min-w-28">Presentation attendees</TH>
                <TH numeric className="min-w-24">Reach</TH>
              </TR>
            </THead>
            <TBody>
              {row.entries.map(({ event, school }, index) => (
                <TR key={`${eventKey(event)}:${index}`}>
                  <TD className="whitespace-nowrap tabular-nums">
                    {eventDateText(event.startDate, event.endDate)}
                  </TD>
                  <TD className="max-w-80 truncate">{event.name}</TD>
                  <TD>
                    <FormatBadge format={event.format} />
                  </TD>
                  <TD numeric className="tabular-nums">
                    {piCount(school.boothConnections)}
                  </TD>
                  <TD numeric className="tabular-nums">
                    {piCount(school.presentationAttendees)}
                  </TD>
                  <TD numeric className="tabular-nums">
                    {piCount(school.registrationsReached)}
                    {school.sharedReach && (
                      <span className="ml-1 text-xs text-fg-muted">shared</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>
      <ul className="grid gap-2 md:hidden">
        {row.entries.map(({ event, school }, index) => (
          <li
            key={`${eventKey(event)}:${index}`}
            className="min-w-0 rounded-md border border-hairline bg-surface p-2.5"
          >
            <div className="truncate font-medium text-fg">{event.name}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted">
              <span className="tabular-nums">{eventDateText(event.startDate, event.endDate)}</span>
              <FormatBadge format={event.format} />
            </div>
            <div className="mt-1.5 grid gap-1">
              <PiCardField label="Booth connections" value={piCount(school.boothConnections)} />
              <PiCardField
                label="Presentation attendees"
                value={piCount(school.presentationAttendees)}
              />
              <PiCardField
                label={school.sharedReach ? 'Shared reach' : 'Reach'}
                value={piCount(school.registrationsReached)}
              />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

export function EventsBySchool({
  rows,
  onClearFilters,
}: {
  rows: readonly SchoolAggregate[];
  /** Optional: offered on the empty state when the page has filters to clear. */
  onClearFilters?: () => void;
}): ReactElement {
  const [sort, setSort] = useRememberedState<SortState<Column>>(
    'pi-events-schools-sort',
    DEFAULT_SORT,
    parseSort,
  );
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Sort, group (Institutions first), then cut the first `limit` rows in that
  // display order so "Show more" never skips a group.
  const groups = useMemo(() => {
    const sorted = [...rows].sort(
      (a, b) =>
        compareValues(valueOf(a, sort.column), valueOf(b, sort.column), sort.dir) ||
        b.events - a.events ||
        a.name.localeCompare(b.name),
    );
    const ordered = splitIntoGroups(sorted, (row) => row.group).flatMap((group) => group.rows);
    return splitIntoGroups(ordered.slice(0, limit), (row) => row.group);
  }, [rows, sort.column, sort.dir, limit]);

  const fullCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) counts.set(row.group.label, (counts.get(row.group.label) ?? 0) + 1);
    return counts;
  }, [rows]);

  if (rows.length === 0) {
    return (
      <PiEmpty
        title="No schools took part in the events matching these filters."
        action={
          onClearFilters ? (
            <Button variant="secondary" onClick={onClearFilters}>
              Clear filters
            </Button>
          ) : undefined
        }
      />
    );
  }

  const shown = Math.min(limit, rows.length);
  const remaining = rows.length - shown;

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const stateOf = (column: Column) => (sort.column === column ? sort.dir : null);
  const onSort = (column: Column) =>
    setSort(
      sort.column === column
        ? { column, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: column === 'school' ? 'asc' : 'desc' },
    );
  const header = (column: Column, label: string, align: 'left' | 'right') => (
    <PiSortHeader
      label={label}
      active={sort.column === column}
      direction={sort.column === column ? sort.dir : column === 'school' ? 'asc' : 'desc'}
      align={align}
      onClick={() => onSort(column)}
    />
  );

  const expandButton = (row: SchoolAggregate) => (
    <PiExpandButton
      expanded={expanded.has(row.key)}
      onClick={() => toggle(row.key)}
      label={`${expanded.has(row.key) ? 'Hide' : 'Show'} events for ${row.name}`}
    />
  );

  return (
    <div className="grid min-w-0 gap-3">
      {/* Desktop */}
      <div className="max-md:hidden">
        <TableWrap frozenHeader>
          <Table>
            <THead>
              <TR>
                <TH className="w-10">
                  <span className="sr-only">Expand</span>
                </TH>
                <TH aria-sort={ariaSort(stateOf('school'))} className="min-w-64">
                  {header('school', 'School', 'left')}
                </TH>
                <TH numeric aria-sort={ariaSort(stateOf('events'))} className="min-w-32">
                  {header('events', 'Events', 'right')}
                </TH>
                <TH numeric aria-sort={ariaSort(stateOf('booth'))} className="min-w-28">
                  {header('booth', 'Booth connections', 'right')}
                </TH>
                <TH numeric aria-sort={ariaSort(stateOf('presentation'))} className="min-w-28">
                  {header('presentation', 'Presentation attendees', 'right')}
                </TH>
                <TH numeric aria-sort={ariaSort(stateOf('reach'))} className="min-w-24">
                  {header('reach', 'Reach', 'right')}
                </TH>
                <TH numeric aria-sort={ariaSort(stateOf('sharedReach'))} className="min-w-28">
                  {header('sharedReach', 'Shared reach', 'right')}
                </TH>
              </TR>
            </THead>
            <TBody>
              {groups.map((group) => (
                <Fragment key={group.label}>
                  <GroupHeaderRow
                    label={group.label}
                    count={fullCounts.get(group.label) ?? group.rows.length}
                    colSpan={COL_SPAN}
                  />
                  {group.rows.map((row) => (
                    <Fragment key={row.key}>
                      <TR>
                        <TD className="w-10 px-1">{expandButton(row)}</TD>
                        <TD className="max-w-96">
                          <div className="min-w-0 truncate">
                            <CompanyName name={row.name} company={row.company} />
                          </div>
                        </TD>
                        <TD numeric className="tabular-nums">
                          <div className="font-medium">{piCount(row.events)}</div>
                          <div className="text-xs whitespace-nowrap text-fg-muted">
                            {eventsSplitText(row)}
                          </div>
                        </TD>
                        <TD numeric className="font-medium tabular-nums">
                          {piCount(row.boothConnections)}
                        </TD>
                        <TD numeric className="tabular-nums">
                          {piCount(row.presentationAttendees)}
                        </TD>
                        <TD numeric className="tabular-nums">
                          {reachText(row)}
                        </TD>
                        <TD numeric className="tabular-nums text-fg-muted">
                          {sharedReachText(row)}
                        </TD>
                      </TR>
                      {expanded.has(row.key) && (
                        <TR>
                          <TD colSpan={COL_SPAN} className="bg-surface-sunken">
                            <div className="max-w-4xl pl-9">
                              <SchoolEventsPanel row={row} />
                            </div>
                          </TD>
                        </TR>
                      )}
                    </Fragment>
                  ))}
                </Fragment>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>

      {/* Phone */}
      {groups.map((group) => (
        <section key={group.label} className="grid min-w-0 gap-2 md:hidden">
          <GroupHeading
            label={group.label}
            count={fullCounts.get(group.label) ?? group.rows.length}
          />
          <PiCardList
            items={group.rows}
            getKey={(row) => row.key}
            render={(row) => (
              <div className="grid min-w-0 gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="min-w-0 truncate">
                      <CompanyName name={row.name} company={row.company} />
                    </div>
                    <div className="text-xs text-fg-muted">{eventsSplitText(row)}</div>
                  </div>
                  <div className="shrink-0">{expandButton(row)}</div>
                </div>
                <div className="grid gap-1">
                  <PiCardField label="Events" value={piCount(row.events)} />
                  <PiCardField label="Booth connections" value={piCount(row.boothConnections)} />
                  <PiCardField
                    label="Presentation attendees"
                    value={piCount(row.presentationAttendees)}
                  />
                  <PiCardField label="Reach" value={reachText(row)} />
                  <PiCardField label="Shared reach" value={sharedReachText(row)} />
                </div>
                {expanded.has(row.key) && (
                  <div className="border-t border-hairline pt-2">
                    <SchoolEventsPanel row={row} />
                  </div>
                )}
              </div>
            )}
          />
        </section>
      ))}

      {remaining > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          <span className="text-xs text-fg-muted tabular-nums">
            {piCount(shown)} of {piCount(rows.length)} schools
          </span>
          <Button
            variant="secondary"
            className="max-sm:h-11"
            onClick={() => setLimit((current) => current + PAGE_SIZE)}
          >
            Show {piCount(Math.min(PAGE_SIZE, remaining))} more
          </Button>
        </div>
      )}
    </div>
  );
}
